/**
 * Global Error Toast Store
 * Centralized state management for API error toasts using Zustand
 * Supports multiple parallel errors, deduplication, and server-down detection
 */

import { create } from 'zustand';

export interface ErrorToast {
  id: string;
  message: string;
  type: 'error' | 'warning' | 'network' | 'server-down';
  category: ErrorCategory;
  timestamp: number;
  duration?: number;
  isPersistent?: boolean; // For server-down errors
  metadata?: {
    url?: string;
    status?: number;
    code?: string;
  };
}

export type ErrorCategory =
  | 'network'           // Network errors, connection failures
  | 'server-down'       // Backend server unavailable
  | 'timeout'           // Request timeout
  | 'client-error'      // 4xx errors
  | 'server-error'      // 5xx errors
  | 'application-error' // Custom backend errors
  | 'unknown';          // Uncategorized errors

interface ErrorToastStore {
  toasts: ErrorToast[];
  serverDownDetected: boolean;
  errorCount: Map<string, number>; // For tracking duplicate errors

  // Actions
  addError: (error: Omit<ErrorToast, 'id' | 'timestamp'>) => string;
  removeError: (id: string) => void;
  clearAll: () => void;
  clearServerDownErrors: () => void;
  setServerDownDetected: (isDown: boolean) => void;

  // Helper methods
  hasError: (message: string) => boolean;
  getErrorCount: (message: string) => number;
}

let toastCounter = 0;

export const useErrorToastStore = create<ErrorToastStore>((set, get) => ({
  toasts: [],
  serverDownDetected: false,
  errorCount: new Map<string, number>(),

  addError: (error) => {
    const state = get();
    const messageHash = error.message.toLowerCase().trim();

    // Generate unique ID
    toastCounter += 1;
    const id = `error-${Date.now()}-${toastCounter}-${Math.random().toString(36).substr(2, 9)}`;

    // Check for duplicates within recent time window (5 seconds)
    const recentDuplicate = state.toasts.find(
      (toast) =>
        toast.message === error.message &&
        Date.now() - toast.timestamp < 5000 &&
        toast.category === error.category
    );

    if (recentDuplicate) {
      // Update error count but don't add duplicate toast
      const currentCount = state.errorCount.get(messageHash) || 0;
      state.errorCount.set(messageHash, currentCount + 1);
      return recentDuplicate.id;
    }

    // For server-down errors, only show one persistent toast
    if (error.type === 'server-down') {
      const existingServerDownToast = state.toasts.find(t => t.type === 'server-down');
      if (existingServerDownToast) {
        return existingServerDownToast.id;
      }
    }

    const newToast: ErrorToast = {
      ...error,
      id,
      timestamp: Date.now(),
      duration: error.duration || (error.isPersistent ? undefined : 7000),
    };

    set((state) => ({
      toasts: [...state.toasts, newToast],
      errorCount: new Map(state.errorCount).set(messageHash, 1),
    }));

    // Auto-remove non-persistent toasts after duration
    if (!newToast.isPersistent && newToast.duration) {
      setTimeout(() => {
        get().removeError(id);
      }, newToast.duration);
    }

    return id;
  },

  removeError: (id) => {
    set((state) => ({
      toasts: state.toasts.filter((toast) => toast.id !== id),
    }));
  },

  clearAll: () => {
    set({ toasts: [], errorCount: new Map() });
  },

  clearServerDownErrors: () => {
    set((state) => ({
      toasts: state.toasts.filter((toast) => toast.type !== 'server-down'),
      serverDownDetected: false,
    }));
  },

  setServerDownDetected: (isDown) => {
    set({ serverDownDetected: isDown });
  },

  hasError: (message) => {
    const state = get();
    return state.toasts.some((toast) => toast.message === message);
  },

  getErrorCount: (message) => {
    const state = get();
    const messageHash = message.toLowerCase().trim();
    return state.errorCount.get(messageHash) || 0;
  },
}));
